import type { PartialReportConfig } from '../../../../../../../../shared/reports/types';
import type { IncludeFilterOption } from '../../types';

type SectionKey = keyof NonNullable<PartialReportConfig['sections']>;
type ToggleKey = keyof NonNullable<PartialReportConfig['toggles']>;
type DependencyDataKey = keyof NonNullable<PartialReportConfig['dependencyData']>;

const includeOptionToSection: Partial<Record<IncludeFilterOption, SectionKey>> = {
  includeDirectDependencies: 'directDependencies',
  includeDeepDependencies: 'deepDependencies',
  includeSnippetDependencies: 'snippetDependencies',
  includeProjectLicense: 'projectDeclaredLicenses',
  includeLicenseScan: 'firstPartyLicenses',
  includeCopyrightList: 'copyrightList',
  includeDependencySummary: 'dependencySummary',
  includeNoticeFiles: 'firstPartyNoticeFiles',
  includeFOSSADependencies: 'fossaDependencies',
  includeOpenVulnerabilities: 'openVulnerabilities',
  includeClosedVulnerabilities: 'closedVulnerabilities',
  includeSupportStatus: 'supportStatus',
  includeFileMatches: 'licenseFileMatches',
  includeConcludedLicenses: 'concludedLicenses',
  includeNonLicenseCopyrights: 'nonLicenseCopyrights',
};

const includeOptionToToggle: Partial<Record<IncludeFilterOption, ToggleKey>> = {
  includeHashAndVersionData: 'useHashAndVersionData',
  excludeUnknownDependencies: 'excludeUnknownDependencies',
};

const includeOptionToDependencyData: Partial<Record<IncludeFilterOption, DependencyDataKey>> = {
  includeLicenseHeaders: 'licenseHeaders',
  includePackageLabels: 'packageLabels',
};

const infoOptionToDependencyData: Record<string, DependencyDataKey> = {
  Project: 'projects',
  Authors: 'authors',
  Description: 'description',
  ProjectUrl: 'homepage',
  Source: 'packageManager',
  PackageDownloadUrl: 'downloadUrl',
  ConcludedLicense: 'concludedLicenses',
  License: 'declaredLicenses',
  OtherLicenses: 'discoveredLicenses',
  Copyrights: 'copyrights',
  LicenseFileURL: 'licenseUrl',
  IssueNotes: 'issueResolutionNotes',
  DependencyPaths: 'dependencyPaths',
  FilePath: 'filePaths',
  NoticeFiles: 'noticeFiles',
  FullTextLicense: 'fullLicenseText',
  LicenseTextAppendix: 'licenseTextAppendix',
  CustomTextLicense: 'licenseHeaders',
};

/**
 * Reverse of mapReportConfigToOptionState.
 * Converts the current filter UI state back into a PartialReportConfig for saving.
 */
export function mapFilterStateToReportConfig(
  includeOptions: IncludeFilterOption[],
  dependencyMetadataOptions: string[],
  packageLabelExclusions: string[],
): PartialReportConfig {
  const sections: NonNullable<PartialReportConfig['sections']> = {};
  const toggles: NonNullable<PartialReportConfig['toggles']> = {};
  const dependencyData: NonNullable<PartialReportConfig['dependencyData']> = {};

  includeOptions.forEach((option) => {
    const sectionKey = includeOptionToSection[option];
    if (sectionKey) {
      sections[sectionKey] = true;
      if (option === 'includeNoticeFiles') {
        dependencyData.noticeFiles = true;
      }
    } else {
      const toggleKey = includeOptionToToggle[option];
      if (toggleKey) {
        toggles[toggleKey] = true;
      } else {
        const dataKey = includeOptionToDependencyData[option];
        if (dataKey) {
          dependencyData[dataKey] = true;
        }
      }
    }
  });

  dependencyMetadataOptions.forEach((option) => {
    const dataKey = infoOptionToDependencyData[option];
    if (dataKey) {
      dependencyData[dataKey] = true;
    }
  });

  return {
    sections,
    toggles,
    dependencyData,
    excludeFields: { packageLabels: packageLabelExclusions },
  };
}
